import React, { useEffect, useMemo, useState } from "react";
import CustomSelect from "../../../components/CustomSelect.jsx";
import { sortDriversByCarNumber, sortPeopleByFirstName } from "../../../lib/driverSorting.js";
import {
    fetchAdminRoles,
    fetchAdminUsers,
    fetchManagerDrivers,
    linkAdminUserDriver,
    unlinkAdminUserDriver,
    updateAdminUserRoles,
} from "../../../lib/managerApi.js";
import { roleLabel } from "../../../auth/roles.js";

const USER_DRIVER_FILTER_OPTIONS = [
    { value: "all", label: "Alle Discord User" },
    { value: "unlinked", label: "Nur ohne Fahrer-Verknuepfung" },
];

function prettyError(err) {
    if (!err) return "Unbekannter Fehler";
    if (typeof err === "string") return err;
    if (err instanceof Error) return err.message || "Fehler";
    if (err.detail) return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
    return String(err);
}

function userLabel(user) {
    return user?.display_name || user?.discord_global_name || user?.discord_username || user?.email || `User #${user?.id || "?"}`;
}

function driverLabel(driver) {
    if (!driver) return "Nicht verknuepft";
    const sd = driver.season_driver;
    const number = sd?.car_number ? `#${sd.car_number}` : "";
    return [number, driver.display_name || driver.iracing_name || driver.label].filter(Boolean).join(" ");
}

function Field({ label, children }) {
    return (
        <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontWeight: 800, fontSize: 13 }}>{label}</span>
            {children}
        </label>
    );
}

function rolePillClass(role) {
    const key = String(role || "").trim().toLowerCase();

    if (key === "admin") return "pill pill--role-admin";
    if (key === "orga") return "pill pill--role-orga";
    if (key === "reko") return "pill pill--role-reko";
    if (key === "reko_lead") return "pill pill--role-reko-lead";
    if (key === "user") return "pill pill--role-user";

    return "pill pill--gesamt";
}

function RolePills({ roles = [] }) {
    if (!roles.length) return <span className="muted">Keine Rollen</span>;

    return (
        <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {roles.map((role) => (
                <span key={role} className={rolePillClass(role)}>
                    {roleLabel(role)}
                </span>
            ))}
        </span>
    );
}

export default function UserManagement() {
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState("");
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [search, setSearch] = useState("");
    const [userDriverFilter, setUserDriverFilter] = useState("all");
    const [driverSearch, setDriverSearch] = useState("");
    const [driverId, setDriverId] = useState("");

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const selectedUser = useMemo(() => {
        return users.find((user) => String(user.id) === String(selectedUserId)) || null;
    }, [users, selectedUserId]);

    const visibleUsers = useMemo(() => {
        const filtered = userDriverFilter !== "unlinked"
            ? users
            : users.filter((user) => !user.linked_driver);
        return sortPeopleByFirstName(filtered);
    }, [userDriverFilter, users]);

    const driverOptions = useMemo(() => {
        return sortDriversByCarNumber(drivers)
            .map((driver) => ({
                value: driver.id,
                label: driverLabel(driver),
            }));
    }, [drivers]);

    function patchUser(nextUser) {
        if (!nextUser?.id) return;
        setUsers((prev) => prev.map((user) => (
            String(user.id) === String(nextUser.id)
                ? { ...user, ...nextUser }
                : user
        )));
    }

    async function loadRoles() {
        const data = await fetchAdminRoles();
        setRoles(Array.isArray(data.roles) ? data.roles : []);
    }

    async function loadUsers() {
        try {
            setLoading(true);
            setError("");
            const data = await fetchAdminUsers({ search });
            const rows = Array.isArray(data.users) ? data.users : [];
            setUsers(rows);
            setSelectedUserId((current) => {
                if (current && rows.some((user) => String(user.id) === String(current))) return current;
                return rows[0]?.id ? String(rows[0].id) : "";
            });
        } catch (err) {
            setError(prettyError(err));
            setUsers([]);
            setSelectedUserId("");
        } finally {
            setLoading(false);
        }
    }

    async function loadDrivers() {
        try {
            setError("");
            const data = await fetchManagerDrivers({
                series_key: "gt3x",
                search: driverSearch,
                driver_status: "all",
                all_seasons: "true",
                include_all_drivers: "true",
            });
            setDrivers(Array.isArray(data.drivers) ? data.drivers : []);
        } catch (err) {
            setError(prettyError(err));
            setDrivers([]);
        }
    }

    useEffect(() => {
        let active = true;

        async function boot() {
            try {
                setLoading(true);
                setError("");
                await loadRoles();
                if (!active) return;
                await loadUsers();
                if (!active) return;
                await loadDrivers();
            } catch (err) {
                if (active) setError(prettyError(err));
            } finally {
                if (active) setLoading(false);
            }
        }

        boot();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const nextRoles = Array.isArray(selectedUser?.roles) ? selectedUser.roles : [];
        setSelectedRoles(nextRoles);
        setDriverId(selectedUser?.linked_driver?.id ? String(selectedUser.linked_driver.id) : "");
    }, [selectedUser]);

    useEffect(() => {
        setSelectedUserId((current) => {
            if (current && visibleUsers.some((user) => String(user.id) === String(current))) return current;
            return visibleUsers[0]?.id ? String(visibleUsers[0].id) : "";
        });
    }, [visibleUsers]);

    function toggleRole(roleKey) {
        const key = String(roleKey || "").trim().toLowerCase();
        if (!key || key === "user") return;

        setSelectedRoles((prev) => {
            const current = new Set(prev);
            if (current.has(key)) {
                current.delete(key);
            } else {
                current.add(key);
            }
            current.add("user");

            if (current.has("reko_lead")) current.add("reko");
            if (current.has("orga") || current.has("reko")) current.add("user");

            return Array.from(current);
        });
    }

    async function saveRoles() {
        if (!selectedUser) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            const data = await updateAdminUserRoles(selectedUser.id, selectedRoles);
            patchUser(data.user);
            setSuccess("Rollen wurden gespeichert. Der User muss sich neu einloggen, damit der Token aktualisiert wird.");
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function linkDriver() {
        if (!selectedUser || !driverId) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            const data = await linkAdminUserDriver(selectedUser.id, driverId);
            patchUser(data.user);
            if (data.user?.linked_driver?.id) {
                setDriverId(String(data.user.linked_driver.id));
            }
            setSuccess("Discord User wurde mit dem Fahrer verknuepft.");
            await loadDrivers();
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function unlinkDriver() {
        if (!selectedUser) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            const data = await unlinkAdminUserDriver(selectedUser.id);
            patchUser(data.user);
            setDriverId("");
            setSuccess("Fahrer-Verknuepfung wurde entfernt.");
            await loadDrivers();
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="rv-page manager-modern-page">
            <section className="rv-page-hero">
                <div className="rv-page-hero__inner">
                    <div>
                        <div className="rv-page-kicker">Admin</div>
                        <h1 className="rv-page-title">Userverwaltung</h1>
                        <p className="rv-page-subtitle">
                            Discord User suchen, Rollen vergeben und bei Bedarf direkt mit einem Fahrer verknuepfen.
                        </p>
                    </div>
                </div>
            </section>

            {error && <div className="rv-message rv-message--error">{error}</div>}
            {success && <div className="rv-message rv-message--ok">{success}</div>}

            <div className="card rv-filter-card" style={{ display: "grid", gap: 14 }}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr)) auto",
                        gap: 10,
                        alignItems: "end",
                    }}
                >
                    <Field label="Discord User suchen">
                        <input
                            className="input"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") loadUsers();
                            }}
                            placeholder="Name, Mail, Discord ID ..."
                        />
                    </Field>
                    <Field label="Verknuepfungsfilter">
                        <CustomSelect
                            value={userDriverFilter}
                            onChange={setUserDriverFilter}
                            options={USER_DRIVER_FILTER_OPTIONS}
                            allowEmpty={false}
                        />
                    </Field>
                    <button className="btn btn--accent" type="button" onClick={loadUsers} disabled={loading}>
                        {loading ? "Lade ..." : "Suchen"}
                    </button>
                </div>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
                    gap: 16,
                    alignItems: "start",
                }}
            >
                <div className="card" style={{ display: "grid", gap: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                        <h2 style={{ marginTop: 0 }}>Discord User</h2>
                        <span className="pill pill--gesamt">{visibleUsers.length}</span>
                    </div>

                    <div style={{ display: "grid", gap: 8, maxHeight: 720, overflow: "auto", paddingRight: 4 }}>
                        {!visibleUsers.length && <div className="muted">Keine User gefunden.</div>}

                        {visibleUsers.map((user) => {
                            const active = String(user.id) === String(selectedUserId);

                            return (
                                <button
                                    key={user.id}
                                    type="button"
                                    onClick={() => setSelectedUserId(String(user.id))}
                                    style={{
                                        textAlign: "left",
                                        border: active
                                            ? "1px solid rgba(228,254,60,.46)"
                                            : "1px solid rgba(255,255,255,.10)",
                                        background: active
                                            ? "rgba(228,254,60,.10)"
                                            : "rgba(255,255,255,.035)",
                                        borderRadius: 14,
                                        padding: 12,
                                        color: "var(--text)",
                                        cursor: "pointer",
                                        display: "grid",
                                        gap: 8,
                                    }}
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                        <strong>{userLabel(user)}</strong>
                                        <span className={user.is_active ? "pill pill--am" : "pill pill--int"}>
                                            {user.is_active ? "Aktiv" : "Inaktiv"}
                                        </span>
                                    </div>
                                    <div className="muted" style={{ fontSize: 13 }}>
                                        {user.discord_username || "ohne Username"} / {user.email || "ohne Mail"}
                                    </div>
                                    <RolePills roles={user.roles} />
                                    <div className="muted" style={{ fontSize: 13 }}>
                                        Fahrer: {driverLabel(user.linked_driver)}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="card" style={{ display: "grid", gap: 18 }}>
                    {!selectedUser ? (
                        <div className="muted">Bitte einen User auswaehlen.</div>
                    ) : (
                        <>
                            <div>
                                <div className="rv-page-kicker">Bearbeiten</div>
                                <h2 style={{ margin: "4px 0 0" }}>{userLabel(selectedUser)}</h2>
                                <p className="muted" style={{ marginTop: 6 }}>
                                    Discord ID {selectedUser.discord_id || "-"} / User ID {selectedUser.id}
                                </p>
                            </div>

                            <section style={{ display: "grid", gap: 12 }}>
                                <h3 style={{ margin: 0 }}>Rollen</h3>

                                <div style={{ display: "grid", gap: 8 }}>
                                    {roles.map((role) => {
                                        const key = role.role_key;
                                        const checked = selectedRoles.includes(key);
                                        const disabled = key === "user";

                                        return (
                                            <label
                                                key={key}
                                                style={{
                                                    border: checked
                                                        ? "1px solid rgba(228,254,60,.40)"
                                                        : "1px solid rgba(255,255,255,.10)",
                                                    background: checked
                                                        ? "rgba(228,254,60,.10)"
                                                        : "rgba(255,255,255,.035)",
                                                    borderRadius: 14,
                                                    padding: 12,
                                                    display: "flex",
                                                    gap: 10,
                                                    alignItems: "center",
                                                    cursor: disabled ? "not-allowed" : "pointer",
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={disabled}
                                                    onChange={() => toggleRole(key)}
                                                />
                                                <span>
                                                    <strong>{role.name || roleLabel(key)}</strong>
                                                    {disabled && <span className="muted"> immer aktiv</span>}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>

                                <div>
                                    <button className="btn btn--accent" type="button" onClick={saveRoles} disabled={busy}>
                                        Rollen speichern
                                    </button>
                                </div>
                            </section>

                            <section style={{ display: "grid", gap: 12 }}>
                                <h3 style={{ margin: 0 }}>Fahrer-Verknuepfung</h3>
                                <div className="muted">
                                    Aktuell: {driverLabel(selectedUser.linked_driver)}
                                </div>

                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "minmax(180px, 1fr) auto",
                                        gap: 10,
                                        alignItems: "end",
                                    }}
                                >
                                    <Field label="Fahrer suchen">
                                        <input
                                            className="input"
                                            value={driverSearch}
                                            onChange={(event) => setDriverSearch(event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") loadDrivers();
                                            }}
                                            placeholder="iRacing Name, ID ..."
                                        />
                                    </Field>
                                    <button className="btn" type="button" onClick={loadDrivers} disabled={busy}>
                                        Suchen
                                    </button>
                                </div>

                                <Field label="Fahrer">
                                    <div className="rv-wide-select">
                                        <CustomSelect
                                            value={driverId}
                                            onChange={setDriverId}
                                            options={driverOptions}
                                            placeholder="Fahrer auswaehlen"
                                        />
                                    </div>
                                </Field>

                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                    <button className="btn btn--accent" type="button" onClick={linkDriver} disabled={busy || !driverId}>
                                        Fahrer verknuepfen
                                    </button>
                                    <button className="btn" type="button" onClick={unlinkDriver} disabled={busy || !selectedUser.linked_driver}>
                                        Verknuepfung entfernen
                                    </button>
                                </div>
                            </section>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
